import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { CheckCircle2, Plus, RotateCcw, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { dispatchCashFlowInvalidate } from '../../common/cashFlowEvents';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import { toIsoYyyyMmDd } from '../../common/transactionDates';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import DialogForm from '../../component/dialog/DialogForm';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import { MobileAccountsList } from '../../component/finance/MobileFinance';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import usePermissions from '../../hooks/usePermissions';
import { EXPENSE_STATUS, updateExpenseStatus } from '../../services/expenseStatusService';
import PayableCreateDialog from './PayableCreateDialog';
import ReceivableCreateDialog from './ReceivableCreateDialog';

// Tela compartilhada de "Contas a pagar" (kind='payable') e "Contas a receber"
// (kind='receivable'), baseada na tabela `transactions`.
//
// Endpoints (`fv-api/transaction/accounts_lister.go`):
//   GET /transactions/payables    — despesas
//   GET /transactions/receivables — receitas
//   params: page, page_size, start_month/end_month (DD/MM/YYYY, por VENCIMENTO),
//           status (pendente|vencido|pago), search, supplier_id (só payables)
//   resposta: { items, total, total_pending, total_overdue, total_paid, page, page_size }
//   item: { id, description, amount, due_date, payment_date, status, type, sale_id,
//           supplier_id?, supplier_name? (só payables, da compra vinculada),
//           customer_id?, customer_name? (só receivables lançadas com cliente) }
//   status "pago" = Pago (despesa) ou Recebido (receita); "vencido" = Pendente com
//   vencimento anterior a hoje (calculado no servidor).
//
// Baixa (só a pagar): PUT /transactions/expense (Prestado Contas + payment_date) e
// desfazer baixa (Pendente). Contas a receber lançadas manualmente (POST /transactions/receivables)
// ficam Pendente/Vencido: a baixa de recebimento ainda não existe nesta fase.

const KIND_CONFIG = {
    payable: {
        endpoint: '/transactions/payables',
        title: 'Contas a pagar',
        subtitle: 'Acompanhe as despesas por vencimento e dê baixa nos pagamentos.',
        paidLabel: 'Pago',
        paidLabelPlural: 'pagas',
        itemLabel: 'contas a pagar',
        searchPlaceholder: 'Descrição da despesa...',
        canSettle: true,
    },
    receivable: {
        endpoint: '/transactions/receivables',
        title: 'Contas a receber',
        subtitle: 'Acompanhe as receitas por vencimento. Receitas já lançadas aparecem como recebidas.',
        paidLabel: 'Recebido',
        paidLabelPlural: 'recebidas',
        itemLabel: 'contas a receber',
        searchPlaceholder: 'Descrição da receita...',
        canSettle: false,
    },
};

function TbLabel({ children }) {
    return (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );
}

const TB_FIELD_RADIUS = '9px';

const MOBILE_PAGE_SIZE = 20;

export default function AccountsList({ kind }) {
    const config = KIND_CONFIG[kind] || KIND_CONFIG.payable;
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const lastReloadRef = useRef(0);

    const statusLabels = {
        pendente: 'Pendente',
        vencido: 'Vencido',
        pago: config.paidLabel,
    };

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPending, setTotalPending] = useState('0,00');
    const [totalOverdue, setTotalOverdue] = useState('0,00');
    const [totalPaid, setTotalPaid] = useState('0,00');
    const [reloadKey, setReloadKey] = useState(0);

    // Filtros aplicados só ao clicar "Buscar"/"Limpar" (ou Enter na busca).
    const defaultStart = dayjs().startOf('month');
    const defaultEnd = dayjs().endOf('month');
    const [draftDateStart, setDraftDateStart] = useState(defaultStart);
    const [draftDateEnd, setDraftDateEnd] = useState(defaultEnd);
    const [draftStatus, setDraftStatus] = useState('');
    const [draftSearch, setDraftSearch] = useState('');

    // Fornecedor (só contas a pagar): filtra pelo fornecedor da compra vinculada à despesa.
    const showSupplier = kind === 'payable';
    const [suppliers, setSuppliers] = useState([]);
    const [draftSupplier, setDraftSupplier] = useState('');
    const [supplierId, setSupplierId] = useState('');

    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [appliedPeriod, setAppliedPeriod] = useState({ start: defaultStart, end: defaultEnd });

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // modal: dar baixa
    const [payModalOpen, setPayModalOpen] = useState(false);
    const [payRow, setPayRow] = useState(null);
    const [paymentDate, setPaymentDate] = useState(dayjs());
    const [statusActionLoading, setStatusActionLoading] = useState(false);

    // modal: desfazer baixa
    const [revertOpen, setRevertOpen] = useState(false);
    const [revertRow, setRevertRow] = useState(null);

    // modal: nova conta a pagar (lançamento manual; só kind='payable')
    const [createOpen, setCreateOpen] = useState(false);
    const canCreatePayable = kind === 'payable' && hasPermission('accounts.payable.create');

    // modal: nova conta a receber (lançamento manual; só kind='receivable'). Exige `accounts.receivable.create` (mesma da rota POST /transactions/receivables).
    const [createReceivableOpen, setCreateReceivableOpen] = useState(false);
    const canCreateReceivable = kind === 'receivable' && hasPermission('accounts.receivable.create');

    const canSettle =
        config.canSettle &&
        hasPermission(kind === 'receivable' ? 'accounts.receivable.update' : 'accounts.payable.update');

    const showSuccess = (text) => {
        setSnackbarSeverity('success');
        setSnackbarMessage(text);
        setOpenSnackbar(true);
    };
    const showError = (text) => {
        setSnackbarSeverity('error');
        setSnackbarMessage(text || 'Não foi possível concluir a operação.');
        setOpenSnackbar(true);
    };

    // Lista completa de fornecedores ativos (GET /stock/suppliers -> [{ id, name }]); /suppliers é paginado.
    useEffect(() => {
        if (!showSupplier) return;
        httpClient.get('/stock/suppliers')
            .then((response) => setSuppliers(Array.isArray(response.data) ? response.data : []))
            .catch(() => setSuppliers([]));
    }, [showSupplier]);

    useEffect(() => {
        let cancelled = false;
        // Mobile: lista contínua ("Carregar mais"); ao recarregar volta para a 1ª página.
        const reloaded = lastReloadRef.current !== reloadKey;
        lastReloadRef.current = reloadKey;
        if (isMobileShell && reloaded && page > 0) {
            setPage(0);
            return undefined;
        }
        const params = {
            page: page + 1,
            page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize,
            start_month: appliedPeriod.start.format('DD/MM/YYYY'),
            end_month: appliedPeriod.end.format('DD/MM/YYYY'),
        };
        if (status) params.status = status;
        if (search) params.search = search;
        if (showSupplier && supplierId) params.supplier_id = supplierId;

        setLoading(true);
        httpClient.get(config.endpoint, { params })
            .then((response) => {
                if (cancelled) return;
                const items = response.data?.items || [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: baixa esvaziou a última página).
                if (!isMobileShell && items.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && page > 0) setRows((prev) => [...prev, ...items]);
                else setRows(items);
                setTotalCount(total);
                setTotalPending(response.data?.total_pending || '0,00');
                setTotalOverdue(response.data?.total_overdue || '0,00');
                setTotalPaid(response.data?.total_paid || '0,00');
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching accounts:', error);
                setRows([]);
                setTotalCount(0);
                setTotalPending('0,00');
                setTotalOverdue('0,00');
                setTotalPaid('0,00');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [config.endpoint, page, pageSize, appliedPeriod, status, search, supplierId, showSupplier, reloadKey, isMobileShell]);

    const applyFilters = () => {
        const nextSearch = draftSearch.trim();
        setStatus(draftStatus);
        setSupplierId(draftSupplier);
        setSearch(nextSearch);
        setDraftSearch(nextSearch);
        setAppliedPeriod({ start: draftDateStart, end: draftDateEnd });
        setPage(0);
    };

    const clearFilters = () => {
        setDraftDateStart(defaultStart);
        setDraftDateEnd(defaultEnd);
        setDraftStatus('');
        setDraftSearch('');
        setDraftSupplier('');
        setStatus('');
        setSearch('');
        setSupplierId('');
        setAppliedPeriod({ start: defaultStart, end: defaultEnd });
        setPage(0);
    };

    const removePeriodFilter = () => {
        setDraftDateStart(defaultStart);
        setDraftDateEnd(defaultEnd);
        setAppliedPeriod({ start: defaultStart, end: defaultEnd });
        setPage(0);
    };
    const removeStatusFilter = () => {
        setDraftStatus('');
        setStatus('');
        setPage(0);
    };
    const removeSupplierFilter = () => {
        setDraftSupplier('');
        setSupplierId('');
        setPage(0);
    };
    const removeSearchFilter = () => {
        setDraftSearch('');
        setSearch('');
        setPage(0);
    };

    // ---- Shell mobile (protótipos `financeiro/contas-pagar.html` e `contas-receber.html`) ----
    const [mobileSearch, setMobileSearch] = useState('');
    // A busca é aplicada sozinha (debounce), sem botão "Buscar".
    useEffect(() => {
        if (!isMobileShell || mobileSearch.trim() === search) return undefined;
        const timer = setTimeout(() => {
            const next = mobileSearch.trim();
            setSearch(next);
            setDraftSearch(next);
            setPage(0);
        }, 350);
        return () => clearTimeout(timer);
    }, [mobileSearch, search, isMobileShell]);

    const today = dayjs().startOf('day');
    const isDraftDefaultPeriod = Boolean(draftDateStart?.isSame(defaultStart, 'day') && draftDateEnd?.isSame(defaultEnd, 'day'));
    const isDefaultPeriod =appliedPeriod.start.isSame(defaultStart, 'day') && appliedPeriod.end.isSame(defaultEnd, 'day');
    const isTodayPeriod = appliedPeriod.start.isSame(today, 'day') && appliedPeriod.end.isSame(today, 'day');
    const isWeekPeriod = appliedPeriod.start.isSame(today, 'day') && appliedPeriod.end.isSame(today.add(7, 'day'), 'day');
    // Chip ativo derivado do filtro aplicado (Todas / Vencidas / Hoje / 7 dias / Pagas).
    let mobileChip = 'all';
    if (status === 'vencido') mobileChip = 'overdue';
    else if (status === 'pago') mobileChip = 'paid';
    else if (!status && isTodayPeriod) mobileChip = 'today';
    else if (!status && isWeekPeriod) mobileChip = 'week';

    const applyMobilePeriod = (start, end) => {
        setDraftDateStart(start);
        setDraftDateEnd(end);
        setAppliedPeriod({ start, end });
    };
    const handleMobileChip = (value) => {
        setPage(0);
        // "Hoje" e "7 dias" filtram por vencimento; os demais saem desses recortes e voltam ao mês.
        const keepPeriod = !(isTodayPeriod || isWeekPeriod);
        const setStatusBoth = (next) => { setStatus(next); setDraftStatus(next); };
        if (value === 'today') {
            setStatusBoth('');
            applyMobilePeriod(today, today);
        } else if (value === 'week') {
            setStatusBoth('');
            applyMobilePeriod(today, today.add(7, 'day'));
        } else {
            setStatusBoth(value === 'overdue' ? 'vencido' : value === 'paid' ? 'pago' : '');
            if (!keepPeriod || value === 'all') applyMobilePeriod(defaultStart, defaultEnd);
        }
    };
    const handleMobileApplyFilters = ({ start, end, supplierId: nextSupplier }) => {
        applyMobilePeriod(start || defaultStart, end || defaultEnd);
        setSupplierId(nextSupplier || '');
        setDraftSupplier(nextSupplier || '');
        setPage(0);
    };
    const handleMobileClearFilters = () => {
        clearFilters();
        setMobileSearch('');
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const refresh = () => setReloadKey((key) => key + 1);

    // ---- dar baixa ----
    const openPayModal = (row) => {
        setPayRow(row);
        setPaymentDate(dayjs());
        setPayModalOpen(true);
    };
    const handleConfirmPay = async () => {
        if (statusActionLoading || !payRow?.id) return;
        const iso = toIsoYyyyMmDd(paymentDate);
        if (!iso) {
            showError('Selecione uma data de pagamento válida.');
            return;
        }
        setStatusActionLoading(true);
        try {
            await updateExpenseStatus({ id: payRow.id, status: EXPENSE_STATUS.PRESTADO_CONTAS, payment_date: iso });
            dispatchCashFlowInvalidate();
            refresh();
            setPayModalOpen(false);
            setPayRow(null);
            showSuccess('Baixa registrada. A saída aparecerá no fluxo de caixa no mês da data de pagamento informada.');
        } catch (e) {
            showError(e.message || 'Erro ao atualizar status.');
        } finally {
            setStatusActionLoading(false);
        }
    };

    // ---- desfazer baixa ----
    const openRevertModal = (row) => {
        setRevertRow(row);
        setRevertOpen(true);
    };
    const handleConfirmRevert = async () => {
        if (statusActionLoading || !revertRow?.id) return;
        setStatusActionLoading(true);
        try {
            await updateExpenseStatus({ id: revertRow.id, status: EXPENSE_STATUS.PENDENTE });
            dispatchCashFlowInvalidate();
            refresh();
            setRevertOpen(false);
            setRevertRow(null);
            showSuccess('Baixa desfeita. A despesa voltou para Pendente.');
        } catch (e) {
            showError(e.message || 'Erro ao atualizar status.');
        } finally {
            setStatusActionLoading(false);
        }
    };

    const columns = [
        {
            name: 'Descrição',
            path: 'description',
            render: (row) => (
                <Box
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280, fontSize: '13px', fontWeight: 700, color: 'text.primary' }}
                    title={row.description}
                >
                    {row.description}
                </Box>
            )
        },
        ...(showSupplier ? [{
            name: 'Fornecedor',
            path: 'supplier_name',
            render: (row) => (
                <Box
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, fontSize: '13px', color: 'text.secondary' }}
                    title={row.supplier_name || undefined}
                >
                    {row.supplier_name || '—'}
                </Box>
            )
        }] : []),
        ...(kind === 'receivable' ? [{
            name: 'Cliente',
            path: 'customer_name',
            render: (row) => (
                <Box
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, fontSize: '13px', color: 'text.secondary' }}
                    title={row.customer_name || undefined}
                >
                    {row.customer_name || '—'}
                </Box>
            )
        }] : []),
        {
            name: 'Vencimento',
            path: 'due_date',
            render: (row) => (
                <Typography
                    component="span"
                    sx={{ fontSize: '13px', fontWeight: row.status === 'vencido' ? 800 : 400, color: row.status === 'vencido' ? 'error.main' : 'text.secondary' }}
                >
                    {row.due_date || '—'}
                </Typography>
            )
        },
        {
            name: kind === 'receivable' ? 'Recebimento' : 'Pagamento',
            path: 'payment_date',
            render: (row) => <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.payment_date || '—'}</Typography>
        },
        {
            name: 'Valor',
            path: 'amount',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 800, color: kind === 'receivable' ? 'success.main' : 'error.main' }}>
                    R$ {formatCurrency(row.amount)}
                </Typography>
            )
        },
        {
            name: 'Status',
            path: 'status',
            render: (row) => {
                const palette = {
                    pendente: { bg: 'warning.bg', color: 'warning.main' },
                    vencido: { bg: 'error.bg', color: 'error.main' },
                    pago: { bg: 'success.bg', color: 'success.main' },
                }[row.status] || { bg: 'background.subtle', color: 'text.secondary' };
                return (
                    <Chip
                        label={statusLabels[row.status] || row.status}
                        size="small"
                        sx={{ bgcolor: palette.bg, color: palette.color, fontWeight: 700 }}
                    />
                );
            }
        }
    ];

    const actions = useMemo(() => {
        const list = [];
        if (canSettle) {
            list.push({
                index: 'dar-baixa',
                ruleShow: ['pendente', 'vencido'],
                icon: <CheckCircle2 size={16} strokeWidth={2} color={theme.palette.success.main} />,
                props: { tooltip: 'Dar baixa', onClick: (row) => openPayModal(row) }
            });
            list.push({
                index: 'desfazer-baixa',
                ruleShow: ['pago'],
                icon: <RotateCcw size={16} strokeWidth={2} color={theme.palette.warning.main} />,
                props: { tooltip: 'Desfazer baixa', onClick: (row) => openRevertModal(row) }
            });
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSettle, theme]);

    const sharedDialogs = (
        <>
            {canCreatePayable && (
                <PayableCreateDialog
                    open={createOpen}
                    suppliers={suppliers}
                    onClose={() => setCreateOpen(false)}
                    onError={showError}
                    onSuccess={() => {
                        setCreateOpen(false);
                        refresh();
                        showSuccess('Conta a pagar lançada como pendente.');
                    }}
                />
            )}

            {canCreateReceivable && (
                <ReceivableCreateDialog
                    open={createReceivableOpen}
                    onClose={() => setCreateReceivableOpen(false)}
                    onError={showError}
                    onSuccess={() => {
                        setCreateReceivableOpen(false);
                        refresh();
                        showSuccess('Conta a receber lançada como pendente.');
                    }}
                />
            )}

            {/* modal: Desfazer baixa */}
            <DialogForm
                open={revertOpen}
                iconType="warning"
                title="Desfazer baixa?"
                content="A despesa volta para Pendente e deixa de contar como saída no fluxo de caixa até que seja dada baixa novamente."
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={statusActionLoading}
                loadingText="Salvando…"
                onClose={() => { if (statusActionLoading) return; setRevertOpen(false); setRevertRow(null); }}
                onSubmit={() => { void handleConfirmRevert(); }}
            />
        </>
    );

    // Shell mobile: listas dos protótipos `financeiro/contas-pagar.html` / `contas-receber.html`.
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget={config.title}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileAccountsList
                    kind={kind}
                    rows={rows}
                    loading={loading}
                    totalCount={totalCount}
                    hasMore={rows.length < totalCount}
                    onLoadMore={() => setPage((current) => current + 1)}
                    totalPending={totalPending}
                    totalOverdue={totalOverdue}
                    chip={mobileChip}
                    onChipChange={handleMobileChip}
                    search={mobileSearch}
                    onSearchChange={setMobileSearch}
                    period={appliedPeriod}
                    supplierId={supplierId}
                    suppliers={suppliers}
                    filtersActive={(!isDefaultPeriod && !isTodayPeriod && !isWeekPeriod) || Boolean(showSupplier && supplierId)}
                    onApplyFilters={handleMobileApplyFilters}
                    onClearFilters={handleMobileClearFilters}
                    canCreate={canCreatePayable || canCreateReceivable}
                    onNew={() => (canCreatePayable ? setCreateOpen(true) : setCreateReceivableOpen(true))}
                    canSettle={canSettle}
                    onPay={openPayModal}
                    onRevert={openRevertModal}
                    payRow={payModalOpen ? payRow : null}
                    paymentDate={paymentDate && paymentDate.isValid() ? paymentDate.format('YYYY-MM-DD') : ''}
                    onPaymentDateChange={(text) => setPaymentDate(text ? dayjs(text) : null)}
                    onClosePay={() => { if (statusActionLoading) return; setPayModalOpen(false); setPayRow(null); }}
                    onConfirmPay={() => { void handleConfirmPay(); }}
                    payLoading={statusActionLoading}
                />
                {sharedDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={config.title}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            {config.title}
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            {config.subtitle}
                        </Typography>
                    </Box>
                    {canCreatePayable && (
                        <Button variant="contained" startIcon={<Plus size={16} strokeWidth={2} />} onClick={() => setCreateOpen(true)}>
                            Nova conta a pagar
                        </Button>
                    )}
                    {canCreateReceivable && (
                        <Button variant="contained" startIcon={<Plus size={16} strokeWidth={2} />} onClick={() => setCreateReceivableOpen(true)}>
                            Nova conta a receber
                        </Button>
                    )}
                </Box>

                <FilterToolbar
                    plain
                    activeCount={(isDraftDefaultPeriod ? 0 : 1) + (draftStatus ? 1 : 0) + (showSupplier && draftSupplier ? 1 : 0)}
                    onClear={clearFilters}
                    onApply={applyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 240px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder={config.searchPlaceholder}
                                value={draftSearch}
                                onChange={(e) => setDraftSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search size={15} strokeWidth={2} /></InputAdornment>,
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={clearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={applyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                    <Box>
                        <TbLabel>Vencimento</TbLabel>
                        <DesktopPeriodSelector
                            start={draftDateStart}
                            end={draftDateEnd}
                            onChange={(start, end) => { setDraftDateStart(start); setDraftDateEnd(end); }}
                            showRefresh={false}
                            showShortcuts={false}
                            dualMonth
                            align="start"
                        />
                    </Box>

                    <Box>
                        <TbLabel>Status</TbLabel>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                                value={draftStatus}
                                displayEmpty
                                onChange={(e) => setDraftStatus(e.target.value)}
                                renderValue={() => statusLabels[draftStatus] || 'Todos'}
                                sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="pendente">Pendente</MenuItem>
                                <MenuItem value="vencido">Vencido</MenuItem>
                                <MenuItem value="pago">{config.paidLabel}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {showSupplier && (
                        <Box>
                            <TbLabel>Fornecedor</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 200, maxWidth: 260 }}>
                                <Select
                                    value={draftSupplier}
                                    displayEmpty
                                    onChange={(e) => setDraftSupplier(e.target.value)}
                                    renderValue={() => suppliers.find((s) => s.id === draftSupplier)?.name || 'Todos'}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {suppliers.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </FilterToolbar>

                <FilterChips>
                    <Chip
                        label={`Vencimento: ${appliedPeriod.start.format('DD/MM/YYYY')} – ${appliedPeriod.end.format('DD/MM/YYYY')}`}
                        size="small"
                        {...(isDefaultPeriod
                            ? { sx: { bgcolor: 'info.bg', color: 'primary.main' } }
                            : {
                                onDelete: removePeriodFilter,
                                deleteIcon: <X size={12} strokeWidth={2.5} />,
                                sx: { bgcolor: 'info.bg', color: 'primary.main' },
                            })}
                    />
                    {status && (
                        <Chip
                            label={`Status: ${statusLabels[status]}`}
                            size="small"
                            onDelete={removeStatusFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {showSupplier && supplierId && (
                        <Chip
                            label={`Fornecedor: ${suppliers.find((s) => s.id === supplierId)?.name || '—'}`}
                            size="small"
                            onDelete={removeSupplierFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={removeSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} conta${totalCount === 1 ? '' : 's'} encontrada${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip label={`Pendente: R$ ${totalPending}`} size="small" sx={{ bgcolor: 'warning.bg', color: 'warning.main' }} />
                    <Chip label={`Vencido: R$ ${totalOverdue}`} size="small" sx={{ bgcolor: 'error.bg', color: 'error.main' }} />
                    <Chip label={`${config.paidLabel}: R$ ${totalPaid}`} size="small" sx={{ bgcolor: 'success.bg', color: 'success.main' }} />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={rows}
                    iconsActions={actions}
                    loading={loading}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel={config.itemLabel}
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {sharedDialogs}
            {/* modal: Dar baixa (exige data de pagamento — regra real da API); no mobile é a folha da lista */}
            {!isMobileShell && (
            <DialogForm
                open={payModalOpen}
                iconType="info"
                title="Dar baixa"
                subtitle={payRow?.description || undefined}
                content="Informe a data de pagamento (obrigatória). A saída aparecerá no fluxo de caixa no mês dessa data."
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={statusActionLoading}
                loadingText="Salvando…"
                onClose={() => { if (statusActionLoading) return; setPayModalOpen(false); setPayRow(null); }}
                onSubmit={() => { void handleConfirmPay(); }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DatePicker
                        label="Data de pagamento"
                        format="DD/MM/YYYY"
                        value={paymentDate}
                        onChange={(v) => setPaymentDate(v || dayjs())}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small',
                                sx: {
                                    '& .MuiPickersOutlinedInput-root': { height: 40, borderRadius: '9px' },
                                    '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionsContainer': { fontSize: '13px' },
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
            </DialogForm>
            )}

        </PageScaffold>
    );
}
